// IsomorphicService (ADR 010) — the SINGLE source of a customer's contactable
// phone + display name. Pure, read-only; runs identically on the admin server,
// the admin client, and the bidder client.
//
// BL-929 guardrail (SACRED intent): after `customer_contacts` was dropped, the
// `customers` row carries an inline `phone_crm_not_for_notify_or_verify` — a
// STAFF-TYPED CRM number that must NEVER address a notification or a
// phone-verification. The only contactable phone is the buyer's
// website-registered `customer_user.phone_number`. `resolvePreferredPhone` is the
// one place that rule lives: its input has no field for the CRM phone, so a caller
// structurally cannot route the CRM number to SMS / verification.

/**
 * The buyer's contactable phone for notifications + verification.
 *
 * Always the website-registered `customer_user.phone_number`; the inline
 * `customers.phone_crm_not_for_notify_or_verify` is CRM-only and is intentionally
 * absent from this input shape.
 *
 * @param input.customerUserPhone - `customer_user.phone_number`, or null/undefined
 *   for a walk-in/placeholder customer with no login (no contactable phone).
 * @returns the trimmed phone, or null when there is no contactable number.
 */
export function resolvePreferredPhone(input: {
  customerUserPhone: string | null | undefined;
}): string | null {
  const phone = input.customerUserPhone?.trim();
  return phone && phone.length > 0 ? phone : null;
}

/**
 * ⚠️ CLERK-/STAFF-FACING ONLY — this resolver can return an EMAIL ADDRESS.
 *
 * Rungs 3–4 can each yield one: `account_name` when a Business row's
 * `company_name` holds an address (BUG-1327 generates the label from it), then
 * `customers.email` — because on a clerk surface an address a clerk can act on
 * beats a blank (BUG-1314 AC 2). That makes the output unsafe for any
 * BIDDER-facing surface: rendering it in a public bid history or an anon-key
 * realtime payload would de-anonymize a buyer to a rival bidder, which is
 * exactly what BUG-1314's RAC 1 forbids.
 *
 * Every call site today is admin-web (staff-authed) or `@auctogon/notifications`
 * addressing the customer about themselves. This lives in the cross-app
 * `@auctogon/core/isomorphic` barrel, so a future bidder-web import is mechanically
 * possible — if you are wiring this into `apps/bidder-web`, stop: use the
 * anonymized `bidderHash` + paddle alias from
 * `packages/realtime/src/services/loaders/bid-ledger-enrichment.ts` instead. The
 * bidder app names its OWN signed-in buyer from the generated
 * `customers.full_name` / `account_name` columns directly, so it never needs a
 * ladder at all (BL-1633 AC 1 / RAC 1) — and
 * `packages/core/src/isomorphic/customers/__tests__/one-resolver-one-sentinel.test.ts`
 * fails the moment `apps/bidder-web` imports this module.
 *
 * (Same guardrail shape as `resolvePreferredPhone` above, which structurally
 * cannot route the CRM phone to SMS.)
 */

/**
 * The ONE empty state for an unresolved customer identity (BL-1633 AC 2).
 *
 * Rendered wherever `resolveCustomerDisplayName` returns `null` — a customer
 * with no person name, no company, no account label and no email. Seven
 * spellings used to say this ('Unknown bidder', 'Unnamed bidder', 'Unknown
 * Customer', …); the same guard test that keeps the resolver out of bidder-web
 * fails on any inline literal, so callers write `?? NO_NAME_ON_FILE`, never
 * their own string. Neutral across bidder / buyer / customer surfaces and
 * truthful about what is known, which is why this spelling won.
 */
export const NO_NAME_ON_FILE = 'No name on file';

/**
 * The ONE stand-in for a missing email beneath {@link NO_NAME_ON_FILE}
 * (BL-2049 AC 4; ADR 058 — Customer Identity § 4, amended 2026-10-03).
 *
 * A nameless customer's row shows their address as its second fact, so an
 * operator can still reach them. When there is no address either, a two-line
 * row says so here instead of leaving the second line blank (one-line copy keeps
 * the bare sentinel): "we don't know who" and "we
 * can't reach them" are two facts. It never appears under a real name — a named
 * customer with no email shows nothing extra.
 */
export const NO_EMAIL_ON_FILE = 'No email on file';

/** The fields `resolveCustomerDisplayName` can draw a name from. Every one is
 *  optional: a caller passes what it has fetched, and absent rungs are skipped
 *  rather than defaulted — that is what lets one function serve a notification
 *  (which knows only the customer row) and the clerk console (which has joined
 *  the login) without either changing the other's answer. */
export interface CustomerDisplayNameInput {
  /** `customers.full_name` — the generated `first_name last_name`. */
  fullName?: string | null;
  /** `customers.company_name`. */
  companyName?: string | null;
  /** `customers.account_name` — the GENERATED label (BUG-1327: `company_name`
   *  for a Business, `first_name last_name` for an Individual). Redundant with
   *  the two rungs above it when a caller supplies them; load-bearing for the
   *  many callers that fetch only this column. */
  accountName?: string | null;
  // BL-1376 REMOVED: `customerUserName` (`customer_user.name`, now
  // `signup_name_not_canonical`). That column is stamped once at signup and
  // never updated, so reading it here meant a buyer who renamed themselves in
  // their profile could still be displayed under a frozen copy. The name now
  // reaches `fullName` directly: the customer bootstrap seeds first/last from
  // the signup name at row-creation time, and the BL-1376 migration backfilled
  // every pre-existing row. Deliberately DELETED rather than made optional
  // (ADR 044) — an optional field is still reachable, and tsc finding every
  // feeder is the point.
  /** `customers.email`. */
  customerEmail?: string | null;
  // BL-1633 REMOVED: `customerUserEmail` (`customer_user.email`). The read
  // position for every customer ATTRIBUTE — name, company, account type,
  // email — is `customers`; `customer_user` is read only for facts about the
  // login itself (AC 7). The rung was also unreachable by construction: it sat
  // BELOW `customerEmail`, and every feeder supplied that rung too, so
  // `firstNonBlank` never fell through to it. Deleted rather than made
  // optional (ADR 044 — Must Consolidate) so tsc names every feeder.
}

/** First non-blank value, trimmed; `null` when every candidate is blank. */
function firstNonBlank(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/**
 * A customer's display name for labels, notification subjects, documents, and
 * every clerk-facing bidder surface. The SINGLE answer to "what do we call this
 * person?" — five surfaces used to answer it five different ways (BUG-1314),
 * and BL-1633 deleted the last three competing ladders so this is the only one.
 *
 * Resolution order, most-authoritative first:
 *   1. `companyName`   — the business, when there is one. Skipped when it IS
 *                        the customer's own `customerEmail` (BUG-1327 parked
 *                        an address there on some Business rows; an address is
 *                        not a company — equality, never an "@" shape test)
 *   2. `fullName`      — the contact person; the name when there is no company
 *   3. `accountName`   — the generated label (BUG-1327); still an address
 *                        when a Business row parked one in `company_name`
 *   4. `customerEmail` — `customers.email`
 *   5. `null`          — nothing on file; the caller renders `NO_NAME_ON_FILE`
 *
 * Company first, both names always (BL-1818; Dan Saper 2026-09-18, amending
 * ADR 058 — Customer Identity § 1). A Business customer with a contact person
 * resolves to the company here; the PERSON reaches the screen as the second
 * fact (`deriveCustomerIdentityLines`), never by reordering these rungs per
 * surface. The order is the same for every account type: an Individual with a
 * company on file leads with it too.
 *
 * A caller that must name the PERSON — the `{RecipientFullName}` template
 * token — omits `companyName`, and the remaining rungs keep person → label →
 * email. That subset is the sanctioned way to ask a different question of the
 * same ladder (as `resolveDocumentParty` does), not a second ladder.
 *
 * **The rung ORDER is the fix (BUG-1314).** `account_name` is a label, not a
 * name: a clerk registering a buyer typed their name into it, but self-service
 * registration wrote the buyer's EMAIL there. Reading it FIRST is why a clerk
 * running a sale saw `michael+t23@…  $4,250` in the bid record where a name
 * belonged; reading `fullName` before it is what fixed that. BL-1376 made the
 * order sufficient on its own by seeding `first_name`/`last_name` at signup
 * (and backfilling every pre-existing row), so a self-registered buyer's name
 * now sits on rung 1.
 *
 * Rung 3 once carried an email-shape guard — "skip `accountName` when it is an
 * address, then use it anyway" — with the login-name rung between the two
 * halves. BL-1376 deleted that middle rung, which left the pair identically
 * `accountName`: the guard could no longer change any output, and BUG-1348
 * removed it. Don't reintroduce it — a shape test on rung 3 has nothing left
 * to lose to.
 *
 * **An email still beats nothing (BUG-1314 AC 2).** On dev, 51 of 127 bidders
 * who have placed a bid have no name anywhere — for them the email rungs are
 * the only thing a clerk can act on when someone disputes a sale, so they are
 * deliberately preferred over a blank or the `NO_NAME_ON_FILE` sentinel.
 *
 * Backward-compatible by construction: a caller passing only
 * `{ fullName, accountName }` (notifications, invoice PDFs) resolves exactly as
 * it always has — the rungs it doesn't supply are skipped, and an email-shaped
 * `accountName` still comes back when there is no name above it.
 *
 * @param input - Whichever name fields the caller has fetched.
 * @returns The best available display name, or `null` when there is none.
 * @sideEffect None — pure function (ADR 010).
 */
export function resolveCustomerDisplayName(input: CustomerDisplayNameInput): string | null {
  return firstNonBlank(
    companyUnlessOwnEmail(input.companyName, input.customerEmail),
    input.fullName,
    input.accountName,
    input.customerEmail,
  );
}

/**
 * The company rung, unless it is the customer's own address. BUG-1327 left an
 * email parked in `company_name` on some Business rows (every local instance
 * equals the login); leading with it would put an address where the name
 * belongs, so equality with `customers.email` — trimmed, case-insensitive —
 * drops the rung. Deliberately NOT a "looks like an email" test: a company
 * field holding a DIFFERENT address is two facts, and BUG-1348 removed the last
 * shape guard for that reason.
 *
 * @param companyName - `customers.company_name`.
 * @param customerEmail - `customers.email`.
 * @returns the company, or `null` when it is blank or the customer's own email.
 */
export function companyUnlessOwnEmail(
  companyName: string | null | undefined,
  customerEmail: string | null | undefined,
): string | null {
  const company = companyName?.trim();
  if (!company) return null;
  const email = customerEmail?.trim().toLowerCase();
  return email && company.toLowerCase() === email ? null : company;
}
